import { styled, Theme, TypographyProps } from "@mui/material"
import { useLocalization } from "@tm/localization"
import { ErpPrice, PriceType } from "@tm/models"
import { CSSProperties } from "react"
import { Typography } from "../../../muiStyled/DataDisplay/typographie"

export function mapPriceColor(type: PriceType, theme: Theme): string {
    switch (type) {
        case PriceType.Strikethrough:
        case PriceType.Purchase:
            return theme.palette.primary.main
        default:
            return ""
    }
}

export function mapPriceFontWeight(type: PriceType): string | number {
    switch (type) {
        case PriceType.Purchase:
            return "bold"
        default:
            return 600
    }
}

export function mapPriceVariant(type: PriceType): TypographyProps["variant"] {
    switch (type) {
        case PriceType.Strikethrough:
            return "label"
        default:
            return "body2"
    }
}

type StyledPriceProps = {
    priceCustomization?: CSSProperties
    componentVariant: "expanded" | "compact"
    priceType: PriceType
}

const StyledPriceTypography = styled(Typography, {
    shouldForwardProp: (prop: string) => !["priceCustomization", "priceType, componentVariant"].includes(prop),
})<StyledPriceProps>(({ theme, priceCustomization, priceType, componentVariant }) => ({
    textAlign: "right",
    whiteSpace: "nowrap",
    lineHeight: 1.2,
    color:
        componentVariant === "compact"
            ? theme.colors?.light
            : (priceCustomization?.color ??
              ((priceType === PriceType.Purchase && theme.overwrites?.components?.partListV2?.articleItem?.erpContainer?.purchasePriceColor) ||
                  mapPriceColor(priceType, theme))),
    fontWeight: componentVariant === "compact" ? "normal" : (priceCustomization?.fontWeight ?? mapPriceFontWeight(priceType)),
    textDecoration: priceCustomization?.textDecoration,
    ...(priceType === PriceType.Strikethrough &&
        !priceCustomization?.textDecoration && {
            textDecoration: "line-through",
        }),
    fontSize: priceCustomization?.fontSize,
    fontStyle: priceCustomization?.fontStyle,
}))

type PriceValueProps = {
    price: ErpPrice
    variant?: TypographyProps["variant"]
    priceCustomization?: { [key: number]: CSSProperties }
    componentVariant?: "expanded" | "compact"
}

export function PriceValue({ price, variant, priceCustomization, componentVariant = "expanded" }: PriceValueProps) {
    const { formatCurrency } = useLocalization()
    const priceCustomizationType = priceCustomization?.[price.type] ?? undefined

    return (
        <StyledPriceTypography
            variant={variant ?? mapPriceVariant(price.type)}
            priceCustomization={priceCustomizationType}
            componentVariant={componentVariant}
            priceType={price.type}
        >
            {formatCurrency(price.value, price.currencySymbol)}
        </StyledPriceTypography>
    )
}
